"use strict";
/**
 * Evaluation Result Types
 *
 * These types represent the output of the feature flag evaluation engine.
 * They include both the computed values and debugging information.
 *
 * Key concept: Tri-state evaluation results
 * - resolved: Flag has a definite value
 * - pending: Flag requires a sticky assignment that hasn't been made yet
 * - error: Evaluation failed
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SegmentMembershipSchema = exports.FlagAuditEventSchema = exports.FlagValuesSchema = exports.AllFlagsEvaluationResultSchema = exports.FlagEvaluationResultSchema = exports.TriStateResultSchema = exports.EvaluationMetadataSchema = exports.PendingRequirementSchema = exports.EvaluationStatus = exports.EvaluationReason = void 0;
exports.resolved = resolved;
exports.pending = pending;
exports.error = error;
exports.extractFlagValues = extractFlagValues;
const zod_1 = require("zod");
/**
 * Reason why a particular value was returned
 */
exports.EvaluationReason = zod_1.z.enum([
    "default", // Used the default value from definition
    "configDefault", // Used the default value from config
    "rule", // Matched a targeting rule
    "rollout", // Included in rollout percentage
    "rolloutExcluded", // Excluded from rollout percentage
    "disabled", // Flag is disabled
    "error", // Error during evaluation
    "dependency", // Value depends on another flag
    "experiment", // Value from experiment assignment
]);
/**
 * Evaluation status - tri-state result
 */
exports.EvaluationStatus = zod_1.z.enum([
    "resolved", // Flag has a definite value
    "pending", // Requires sticky assignment (call trackExposure)
    "error", // Evaluation failed
]);
/**
 * Pending requirement - what's needed to resolve a pending flag
 */
exports.PendingRequirementSchema = zod_1.z.object({
    /**
     * Type of pending requirement
     */
    type: zod_1.z.literal("stickyAssignment"),
    /**
     * The experiment key that needs assignment
     */
    experimentKey: zod_1.z.string(),
    /**
     * The run ID for the experiment
     */
    runId: zod_1.z.string(),
    /**
     * The exposure event name to call
     */
    exposureEventName: zod_1.z.string(),
});
/**
 * Evaluation metadata - debugging information
 */
exports.EvaluationMetadataSchema = zod_1.z.object({
    /**
     * The flag key that was evaluated
     */
    flagKey: zod_1.z.string(),
    /**
     * ID of the rule that matched (if reason is "rule")
     */
    ruleId: zod_1.z.string().optional(),
    /**
     * Experiment key if this flag is part of an experiment
     */
    experimentKey: zod_1.z.string().optional(),
    /**
     * Variant if this is an experiment result
     */
    variant: zod_1.z.string().optional(),
    /**
     * Timestamp when this was evaluated
     */
    evaluatedAt: zod_1.z.number(),
});
/**
 * Tri-state evaluation result for a single flag
 *
 * This is the new primary result type that supports pending states.
 */
exports.TriStateResultSchema = zod_1.z.discriminatedUnion("status", [
    zod_1.z.object({
        status: zod_1.z.literal("resolved"),
        value: zod_1.z.unknown(),
        metadata: exports.EvaluationMetadataSchema,
    }),
    zod_1.z.object({
        status: zod_1.z.literal("pending"),
        required: exports.PendingRequirementSchema,
        metadata: exports.EvaluationMetadataSchema.optional(),
    }),
    zod_1.z.object({
        status: zod_1.z.literal("error"),
        reason: zod_1.z.string(),
        metadata: exports.EvaluationMetadataSchema.optional(),
    }),
]);
/**
 * Helper to create a resolved result
 */
function resolved(value, metadata) {
    return { status: "resolved", value, metadata };
}
/**
 * Helper to create a pending result
 */
function pending(required, metadata) {
    return { status: "pending", required, metadata };
}
/**
 * Helper to create an error result
 */
function error(reason, metadata) {
    return { status: "error", reason, metadata };
}
/**
 * Result of evaluating a single flag
 *
 * The Zod schema uses z.unknown() for the value field because it needs to
 * handle deserialization from Firestore where the type is not known at parse time.
 * For type-safe usage, use TypedFlagEvaluationResult<T> instead.
 */
exports.FlagEvaluationResultSchema = zod_1.z.object({
    /**
     * The flag key that was evaluated
     */
    flagKey: zod_1.z.string(),
    /**
     * The computed value
     */
    value: zod_1.z.unknown(),
    /**
     * Why this value was returned
     */
    reason: exports.EvaluationReason,
    /**
     * ID of the rule that matched (if reason is "rule")
     */
    matchedRuleId: zod_1.z.string().optional(),
    /**
     * IDs of segments the user is in (for debugging)
     */
    matchedSegments: zod_1.z.array(zod_1.z.string()).optional(),
    /**
     * Flag keys this evaluation depended on
     */
    dependencyChain: zod_1.z.array(zod_1.z.string()).optional(),
    /**
     * Error message if evaluation failed
     */
    error: zod_1.z.string().optional(),
    /**
     * Timestamp when this was evaluated
     */
    evaluatedAt: zod_1.z.number(),
});
/**
 * Result of evaluating all flags for a user
 */
exports.AllFlagsEvaluationResultSchema = zod_1.z.object({
    /**
     * Map of flag key to evaluation result
     */
    flags: zod_1.z.record(zod_1.z.string(), exports.FlagEvaluationResultSchema),
    /**
     * Context version used for this evaluation
     */
    userContextVersion: zod_1.z.number(),
    /**
     * System context version used for this evaluation
     */
    systemContextVersion: zod_1.z.number(),
    /**
     * Timestamp when evaluation started
     */
    evaluatedAt: zod_1.z.number(),
    /**
     * Duration of evaluation in milliseconds
     */
    evaluationDurationMs: zod_1.z.number().optional(),
});
/**
 * Simplified flag values map - just the values without metadata
 * This is what most consumers will use
 *
 * The Zod schema uses z.unknown() for values because it needs to handle
 * deserialization from Firestore where types are not known at parse time.
 * For type-safe usage, use TypedFlagValues<TRegistry> instead.
 */
exports.FlagValuesSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.unknown());
/**
 * Extract just the values from an evaluation result
 */
function extractFlagValues(result) {
    const values = {};
    for (const [key, evaluation] of Object.entries(result.flags)) {
        values[key] = evaluation.value;
    }
    return values;
}
/**
 * Audit event for flag changes
 */
exports.FlagAuditEventSchema = zod_1.z.object({
    /**
     * Unique event ID
     */
    id: zod_1.z.string(),
    /**
     * Type of action performed
     */
    action: zod_1.z.enum(["create", "update", "delete"]),
    /**
     * Type of entity that was changed
     */
    entityType: zod_1.z.enum(["flag", "segment", "config"]),
    /**
     * ID of the entity that was changed
     */
    entityId: zod_1.z.string(),
    /**
     * Previous value (for updates and deletes)
     */
    previousValue: zod_1.z.unknown().optional(),
    /**
     * New value (for creates and updates)
     */
    newValue: zod_1.z.unknown().optional(),
    /**
     * Who performed this action
     */
    performedBy: zod_1.z.string(),
    /**
     * When this action was performed
     */
    performedAt: zod_1.z.number(),
    /**
     * Environment this change was made in
     */
    environment: zod_1.z.string(),
});
/**
 * Segment membership result
 */
exports.SegmentMembershipSchema = zod_1.z.object({
    /**
     * Segment ID
     */
    segmentId: zod_1.z.string(),
    /**
     * Whether the user is a member
     */
    isMember: zod_1.z.boolean(),
    /**
     * Reason for membership status
     */
    reason: zod_1.z.string().optional(),
});
//# sourceMappingURL=evaluation.js.map